/**
 * Editor Field Styles Configuration
 *
 * Centralized styles for all editor field components.
 * Change values here to update styles across all sections.
 *
 * Sprint: screen-editor-header
 * Task: Unified field styling
 */

import type { SxProps, Theme } from '@mui/material';

/**
 * =============================================================================
 * DEBUG / TESTING SETTINGS
 * Set to false before production release!
 * =============================================================================
 */

/**
 * Show tooltip with field path on hover (for debugging/testing)
 *
 * @example
 * Hover over "font size" → shows "navigationBar.back.content.styles.fontSize"
 *
 * TODO: Set to false before production release
 */
export const SHOW_FIELD_PATH_TOOLTIP = true;

/**
 * =============================================================================
 * MAIN FIELD STYLES (Back button, Skip button, etc.)
 * =============================================================================
 */

/**
 * Height for main input fields (buttons, primary inputs)
 */
export const MAIN_FIELD_HEIGHT = 36;

/**
 * Font size for main input fields
 */
export const MAIN_FIELD_FONT_SIZE = '0.875rem';

/**
 * =============================================================================
 * STYLE SECTION FIELD STYLES (font size, line height, colors, etc.)
 * =============================================================================
 */

/**
 * Height for style section input fields
 */
export const STYLE_FIELD_HEIGHT = 28;

/**
 * Font size for style section input fields
 */
export const STYLE_FIELD_FONT_SIZE = '0.75rem';

/**
 * @deprecated Use STYLE_FIELD_HEIGHT instead
 */
export const FIELD_INPUT_HEIGHT = STYLE_FIELD_HEIGHT;

/**
 * @deprecated Use STYLE_FIELD_FONT_SIZE instead
 */
export const FIELD_FONT_SIZE = STYLE_FIELD_FONT_SIZE;

/**
 * Label font size for style fields (smaller)
 */
export const FIELD_LABEL_FONT_SIZE = '0.7rem';

/**
 * Label font size for main fields (larger - Back, Skip, Page Indicator)
 */
export const MAIN_FIELD_LABEL_FONT_SIZE = '0.875rem';

/**
 * Checkbox size (width/height)
 */
export const CHECKBOX_SIZE = 12;

/**
 * Label row height for style fields (ensures alignment in grid)
 * Matches checkbox height (12px) + padding (4px) = 16px
 */
export const STYLE_FIELD_LABEL_HEIGHT = 16;

/**
 * Checkbox border width
 */
export const CHECKBOX_BORDER_WIDTH = '1.5px';

/**
 * Color picker size
 */
export const COLOR_PICKER_SIZE = 20;

/**
 * =============================================================================
 * MAIN FIELD SX STYLES (Back button, Skip button, etc.)
 * =============================================================================
 */

/**
 * Main input field styles (TextField, NumberInput) for primary fields
 * Uses larger sizing for better visibility
 */
export const mainFieldInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': {
    height: MAIN_FIELD_HEIGHT,
    minHeight: MAIN_FIELD_HEIGHT,
    fontSize: MAIN_FIELD_FONT_SIZE,
  },
  '& .MuiOutlinedInput-root': {
    height: MAIN_FIELD_HEIGHT,
    minHeight: MAIN_FIELD_HEIGHT,
  },
  '& .MuiInputBase-input': {
    padding: '6px 12px',
  },
};

/**
 * =============================================================================
 * STYLE SECTION SX STYLES (font size, line height, colors, etc.)
 * =============================================================================
 */

/**
 * Common input field styles (TextField, NumberInput) for style section
 * Uses standard variant (underline only, no border)
 */
export const fieldInputSx: SxProps<Theme> = {
  '& .MuiInput-root': {
    fontSize: STYLE_FIELD_FONT_SIZE,
  },
  '& .MuiInput-input': {
    padding: '4px 0',
  },
};

/**
 * Select field styles (standard variant - underline only)
 */
export const fieldSelectSx: SxProps<Theme> = {
  fontSize: FIELD_FONT_SIZE,
  '& .MuiSelect-select': {
    padding: '4px 0',
  },
};

/**
 * MenuItem styles for Select
 */
export const fieldMenuItemSx: SxProps<Theme> = {
  fontSize: FIELD_FONT_SIZE,
};

/**
 * Color picker input styles
 */
export const colorPickerSx: SxProps<Theme> = {
  width: COLOR_PICKER_SIZE,
  height: COLOR_PICKER_SIZE,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 0.5,
  padding: 0,
};

/**
 * Label styles for style fields (smaller)
 */
export const fieldLabelSx: SxProps<Theme> = {
  fontSize: FIELD_LABEL_FONT_SIZE,
};

/**
 * Label styles for main fields (larger) - enabled state
 */
export const mainFieldLabelEnabledSx: SxProps<Theme> = {
  fontSize: MAIN_FIELD_LABEL_FONT_SIZE,
  fontWeight: 400,
  color: 'text.primary',
};

/**
 * Label styles for main fields (larger) - disabled state
 */
export const mainFieldLabelDisabledSx: SxProps<Theme> = {
  fontSize: MAIN_FIELD_LABEL_FONT_SIZE,
  fontWeight: 300,
  color: 'text.secondary',
};

/**
 * Unchecked checkbox icon styles
 */
export const checkboxIconSx: SxProps<Theme> = {
  width: CHECKBOX_SIZE,
  height: CHECKBOX_SIZE,
  border: CHECKBOX_BORDER_WIDTH + ' solid',
  borderColor: 'text.secondary',
  borderRadius: 0.5,
  backgroundColor: 'transparent',
};

/**
 * Checked checkbox icon styles
 */
export const checkboxCheckedIconSx: SxProps<Theme> = {
  width: CHECKBOX_SIZE,
  height: CHECKBOX_SIZE,
  border: CHECKBOX_BORDER_WIDTH + ' solid',
  borderColor: 'text.primary',
  borderRadius: 0.5,
  backgroundColor: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'text.primary',
  fontSize: 8,
};

/**
 * Checkbox container styles
 */
export const checkboxSx: SxProps<Theme> = {
  padding: 0.25,
};

/**
 * =============================================================================
 * EXPANDED SECTION STYLES (Style settings panels, collapsible sections)
 * =============================================================================
 */

/**
 * Background color for expanded style sections.
 * Uses MUI theme action.hover for subtle visibility in both light/dark modes.
 * Used by: V2TemplateTextElement and other expandable style panels.
 */
export const EXPANDED_SECTION_BGCOLOR = 'action.hover';

/**
 * Styles for the expanded section wrapper box
 */
export const expandedSectionSx: SxProps<Theme> = {
  p: 1.5,
  borderRadius: 1,
  bgcolor: EXPANDED_SECTION_BGCOLOR,
};

/**
 * Styles for toggle button in normal state
 */
export const expandedToggleButtonSx: SxProps<Theme> = {
  width: 40,
  height: 40,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  color: 'action.active',
  bgcolor: 'transparent',
  '&:hover': {
    borderColor: 'primary.main',
    bgcolor: EXPANDED_SECTION_BGCOLOR,
  },
};

/**
 * Styles for toggle button in expanded state
 */
export const expandedToggleButtonActiveSx: SxProps<Theme> = {
  width: 40,
  height: 40,
  border: '1px solid',
  borderColor: 'primary.main',
  borderRadius: 1,
  color: 'primary.main',
  bgcolor: EXPANDED_SECTION_BGCOLOR,
  '&:hover': {
    borderColor: 'primary.main',
    bgcolor: EXPANDED_SECTION_BGCOLOR,
  },
};
